import { cloneDeep, uniqBy } from "lodash";
import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ReadyState } from "react-use-websocket";
import { useImmer } from "use-immer";

import {
  GetProcessesResponseBodyType,
  ProcessStatusTableDto,
  WebsocketProcessMessage,
} from "@intelligems/ig-types";
import { useQueryClient } from "@tanstack/react-query";

import { useGetOrg } from "../api/organizations/get-organization";
import { personalizationKeys } from "../api/personalizations/use-dashboard-personalization-or-default";
import { useGetProcesses } from "../api/processes/get-processes";
import {
  experienceKeys,
  experienceProductsKeys,
  historyKeys,
  organizationsKeys,
  processesKeys,
  scheduleKeys,
} from "../api/queryKeys";
import { useWebsocket } from "../hooks/websocket";
import {
  getIgnoreUpdateDetected,
  isDialogOpen,
  setDialog,
  setEventProgress,
} from "../store/slices/global";
import { useIsEditPage } from "../utils/constants";

export const WebsocketProvider: FC = () => {
  const [productProgressAttempts, setProductProgressAttempts] =
    useImmer<number>(0);
  const organization = useGetOrg();
  const websocket = useWebsocket(organization.data?.org.id);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isEditPage = useIsEditPage();
  const requestAllEventProgresses = () => {
    websocket.sendJsonMessage({ action: "requestAllEventProgresses" });
  };

  const ignoreUpdateDetected = useSelector(getIgnoreUpdateDetected);
  const isModifiedDialogOpen = useSelector(isDialogOpen("updatedDetected"));
  const isOpenPreviewDialogOpen = useSelector(
    isDialogOpen("redirectToPluginDialog")
  );

  const processes = useGetProcesses();

  const [activelySavingExperiments, setActivelySavingExperiments] = useImmer<
    Record<string, true>
  >({});

  useEffect(() => {
    if (websocket.readyState === ReadyState.OPEN) {
      console.log("open event");
      requestAllEventProgresses();
    }
  }, [websocket.readyState]);

  useEffect(() => {
    if (!isEditPage && isModifiedDialogOpen && !isOpenPreviewDialogOpen) {
      dispatch(setDialog({ dialog: "updatedDetected", open: false }));
    }
  }, [isEditPage, isModifiedDialogOpen, isOpenPreviewDialogOpen]);

  useEffect(() => {
    if (processes.isSuccess) {
      for (const process of processes.data
        .processes as ProcessStatusTableDto[]) {
        if (process.type === "putExperience") {
          for (const id of Object.keys(activelySavingExperiments)) {
            if (processes.data.processes.find((_) => _.id === id)) {
              setActivelySavingExperiments((draft) => {
                draft[process.id] = true;
                return draft;
              });
            } else {
              void queryClient.invalidateQueries({
                queryKey: experienceProductsKeys.one(id),
              });
              setActivelySavingExperiments((draft) => {
                delete draft[id];
                return draft;
              });
            }
          }
        }
      }
    }
  }, [processes.data, processes.isSuccess]);

  useEffect(() => {
    let eventProgressTimeout: NodeJS.Timer | undefined = undefined;
    if (websocket.lastJsonMessage) {
      if (websocket.lastJsonMessage.type === "eventProgress") {
        dispatch(setEventProgress(websocket.lastJsonMessage));
        if (websocket.lastJsonMessage.reason === "response") {
          eventProgressTimeout = setTimeout(() => {
            setProductProgressAttempts((draft) => {
              draft++;
              return draft;
            });
            if (productProgressAttempts < 20) {
              requestAllEventProgresses();
            }
          }, 5000);
          if (websocket.lastJsonMessage.progress >= 1) {
            clearTimeout(eventProgressTimeout);
          }
        }
      } else if (websocket.lastJsonMessage.type === "configUpdate") {
        void queryClient.invalidateQueries({
          queryKey: historyKeys.allConfigEvents,
        });
      } else if (websocket.lastJsonMessage.type === "processUpdate") {
        const lastJsonMessage =
          websocket.lastJsonMessage as WebsocketProcessMessage;
        if (lastJsonMessage.action === "start") {
          queryClient.setQueryData<GetProcessesResponseBodyType>(
            processesKeys.one(organization.data.org.id),
            (data) => {
              return {
                processes: uniqBy(
                  [
                    lastJsonMessage.process as ProcessStatusTableDto,
                    ...(data?.processes || []),
                  ],
                  (_) => _.type
                ),
              };
            }
          );
        } else if (lastJsonMessage.action === "update") {
          queryClient.setQueryData<GetProcessesResponseBodyType>(
            processesKeys.one(organization.data.org.id),
            (data) => {
              if (data?.processes) {
                const index = (data?.processes || []).findIndex(
                  (process) => process.type === lastJsonMessage.process.type
                );
                if (index > -1) {
                  const processes: ProcessStatusTableDto[] = cloneDeep(
                    data.processes
                  );
                  processes[index] =
                    lastJsonMessage.process as ProcessStatusTableDto;
                  return {
                    processes,
                  };
                }
              }

              return {
                processes: uniqBy(data?.processes || [], (_) => _.type),
              };
            }
          );
        } else {
          if (lastJsonMessage.process.type === "putExperience") {
            void queryClient.invalidateQueries({
              queryKey: experienceProductsKeys.one(lastJsonMessage.process.id),
            });
            setActivelySavingExperiments((draft) => {
              delete draft[lastJsonMessage.process.id];
              return draft;
            });
          }
          queryClient.setQueryData<GetProcessesResponseBodyType>(
            processesKeys.one(organization.data.org.id),
            (data) => {
              if (
                data?.processes.some(
                  (p) =>
                    p.id === undefined &&
                    p.type === lastJsonMessage.process.type
                )
              ) {
                return {
                  processes:
                    data?.processes.filter(
                      (process) => process.type !== lastJsonMessage.process.type
                    ) || [],
                };
              }
              return {
                processes:
                  data?.processes.filter(
                    (process) => process.id !== lastJsonMessage.process.id
                  ) || [],
              };
            }
          );
        }
      } else if (websocket.lastJsonMessage.type === "entityModification") {
        if (
          isEditPage &&
          !isOpenPreviewDialogOpen &&
          !ignoreUpdateDetected &&
          !websocket.lastJsonMessage.testProducts
        ) {
          setTimeout(
            () =>
              dispatch(setDialog({ dialog: "updatedDetected", open: true })),
            0
          );
        } else {
          if (websocket.lastJsonMessage.experienceId) {
            void queryClient.invalidateQueries({
              queryKey: experienceKeys.one(
                websocket.lastJsonMessage.experienceId
              ),
            });
            void queryClient.invalidateQueries({
              queryKey: experienceProductsKeys.one(
                websocket.lastJsonMessage.experienceId
              ),
            });
            void queryClient.invalidateQueries({
              queryKey: scheduleKeys.one(
                websocket.lastJsonMessage.experienceId
              ),
            });
          } else if (websocket.lastJsonMessage.campaignId) {
            void queryClient.invalidateQueries({
              queryKey: personalizationKeys.one(
                websocket.lastJsonMessage.campaignId
              ),
            });
          }
        }
        if (websocket.lastJsonMessage.experienceId) {
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.experimentList,
          });
        } else if (websocket.lastJsonMessage.campaignId) {
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.personalizationList,
          });

          void queryClient.invalidateQueries({
            queryKey: personalizationKeys.all(true),
          });
          void queryClient.invalidateQueries({
            queryKey: personalizationKeys.all(false),
          });
        }
        if (websocket.lastJsonMessage.message) {
          toast.success(websocket.lastJsonMessage.message);
        }
      } else if (
        websocket.lastJsonMessage.type === "syncPricingFunctionComplete"
      ) {
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.cartTransforms(organization.data.org.id),
        });
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.metafield(organization.data.org.id),
        });
        toast.success("Pricing Function Synced");
      }
    }
    return () => {
      if (eventProgressTimeout) {
        clearTimeout(eventProgressTimeout);
      }
    };
  }, [websocket.lastJsonMessage, organization.data.org.id]);

  return null;
};
